import { cookieStorage, makePersisted } from "@solid-primitives/storage";
import { clientOnly } from "@solidjs/start";
import type { Asset, Concept, ConceptId } from "@storytell/types";
import { StButton } from "@storytell/ui/buttons";
import Resizable from "corvu/resizable";
import dayjs from "dayjs";
import localizedFormat from "dayjs/plugin/localizedFormat";
import { TbChevronLeft, TbDots, TbMaximize, TbMaximizeOff } from "solid-icons/tb";
import { Show, Suspense, createEffect, createMemo, createSignal, onMount } from "solid-js";
import { createStore } from "solid-js/store";
import { useWire } from "~/wire";
import type { FilterBuilder } from "../filtering/filterBuilder";
import type { EntitySelection } from "../knowledge/useEntitySelection";
import { ProcessingSpinner } from "../ui/ProcessingSpinner";
import { NavigationConceptsPaneDetails } from "./NavigationConceptsPaneDetails";
import { NavigationConceptsPaneTable } from "./NavigationConceptsPaneTable";
dayjs.extend(localizedFormat);

const ConceptsGraph = clientOnly(() =>
  import("~/components/concepts/ConceptsGraph").then((m) => ({ default: m.ConceptsGraph })),
);

export const NavigationConceptsPane = (props: {
  mount?: Node;
  search: string;
  selection: EntitySelection;
  selectedConcept: ConceptId | "";
  setSelectedConcept: (conceptId: ConceptId | "") => void;

  filterBuilder: FilterBuilder<Asset | Concept>;
}) => {
  const wire = useWire();

  const [graphSelectedConcept, setGraphSelectedConcept] = createSignal<ConceptId | "">("");
  const [panelCollapsed, setPanelCollapsed] = makePersisted(createSignal(false), {
    name: "cgcol:v1",
    storage: cookieStorage,
  });

  const concepts = createMemo(() =>
    wire.concepts
      .search(props.search)
      .filter(props.filterBuilder.buildFilterFunction())
      .sort((a, b) => b.score - a.score),
  );

  const [score, setScore] = createStore({
    min: 0,
    max: 0,
    current: Math.min(Math.round(concepts().length / 2), 20),
  });

  return (
    <div class="flex flex-col h-full space-y-4">
      <Show
        when={props.selectedConcept}
        fallback={
          <div class="h-[calc(100vh-12rem)] max-h-[calc(100vh-12rem)] px-4">
            <Show
              when={wire.concepts.searchIds("").length > 0}
              fallback={
                <div class="h-full flex flex-col items-center justify-center text-center p-8">
                  <div class="mb-6">
                    {/* Spinner for generating concepts */}
                    <div class="flex justify-center mb-6">
                      <ProcessingSpinner class="w-8 h-8 border-2 border-brand border-t-transparent" />
                    </div>

                    <h3 class="text-lg font-semibold text-primary-text mb-2">Generating Concepts</h3>
                    <p class="text-secondary-text">We are generating Concepts based on your Assets</p>
                    <p class="text-secondary-text text-sm mt-1">This typically takes a few minutes</p>
                  </div>
                </div>
              }
            >
              <Resizable orientation="vertical" class="h-full max-h-full">
                <Resizable.Panel
                  initialSize={0.5}
                  minSize={0.2}
                  collapsible
                  collapsedSize={"40px"}
                  class="overflow-hidden rounded-xl relative"
                >
                  {(panel) => {
                    onMount(() => {
                      createEffect(() => {
                        if (!panelCollapsed()) {
                          if (panel.collapsed) {
                            panel.expand();
                            panel.resize(0.5);
                          }
                        } else {
                          if (!panel.collapsed) {
                            panel.collapse();
                          }
                        }
                      });
                    });
                    return (
                      <Show
                        when={!panel.collapsed}
                        fallback={
                          <div class="flex items-center justify-start p-1">
                            <StButton
                              icon={TbMaximize}
                              onClick={() => {
                                setPanelCollapsed(false);
                              }}
                            >
                              Show ConceptGraph™
                            </StButton>
                          </div>
                        }
                      >
                        <ConceptsGraph
                          score={{ ...score }}
                          setScore={setScore}
                          concepts={concepts()}
                          graphSelectedConcept={graphSelectedConcept()}
                          onConceptClick={props.setSelectedConcept}
                          onGraphConceptClick={setGraphSelectedConcept}
                          search={props.search}
                          buildFilterFunction={props.filterBuilder.buildFilterFunction()}
                        />

                        <div class="dark absolute top-2 left-2 z-[9999]">
                          <StButton
                            icon={TbMaximizeOff}
                            onClick={() => {
                              setPanelCollapsed(true);
                            }}
                          >
                            Hide Graph
                          </StButton>
                        </div>
                      </Show>
                    );
                  }}
                </Resizable.Panel>
                <Resizable.Handle aria-label="Resize Handle" class="group basis-3 py-0.75 relative">
                  <div class="size-full rounded-sm transition-colors group-data-active:bg-black/10 group-data-dragging:bg-black/20 dark:group-data-active:bg-white/10 dark:group-data-dragging:bg-white/20" />
                  <div class="absolute top-0.5 left-[calc(50%-1rem)] w-8 bottom-0.5 bg-black/20 group-data-dragging:bg-black/30 dark:bg-white/20 dark:group-data-dragging:bg-white/30 rounded-full grid place-content-center">
                    <TbDots class="text-black/30 dark:text-white/30" />
                  </div>
                </Resizable.Handle>
                <Resizable.Panel initialSize={0.5} minSize={0.2} class="rounded-lg overflow-y-auto">
                  <NavigationConceptsPaneTable
                    score={score.current}
                    concepts={concepts()}
                    selection={props.selection}
                    mount={props.mount}
                    search={props.search}
                    buildFilterFunction={props.filterBuilder.buildFilterFunction()}
                    selectedConcept={graphSelectedConcept()}
                    viewConceptDetails={props.setSelectedConcept as (id: string) => void}
                    viewConceptInGraph={setGraphSelectedConcept}
                  />
                </Resizable.Panel>
              </Resizable>
            </Show>
          </div>
        }
      >
        <Suspense fallback={<div class="h-full w-full flex items-center justify-center">Loading...</div>}>
          <NavigationConceptsPaneDetails
            filterBuilder={props.filterBuilder}
            variant="pane"
            id={props.selectedConcept}
            onConceptClick={props.setSelectedConcept}
          />
        </Suspense>
      </Show>
    </div>
  );
};
